import { Head, Link, router } from '@inertiajs/react'
import { Bell, Check, CheckCheck, MessageCircle, AtSign, Users, ExternalLink } from 'lucide-react'
import { MainLayout } from '@/components/layouts'
import type { NotificationItem } from '@/types'

interface NotificationsProps {
  notifications: NotificationItem[]
  unread_count: number
}

function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffInSeconds < 60) return "à l'instant"
  if (diffInSeconds < 3600) return `il y a ${Math.floor(diffInSeconds / 60)} min`
  if (diffInSeconds < 86400) return `il y a ${Math.floor(diffInSeconds / 3600)} h`
  if (diffInSeconds < 604800) return `il y a ${Math.floor(diffInSeconds / 86400)} j`

  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
}

function getNotificationIcon(type: string) {
  switch (type) {
    case 'mention': return <AtSign className="w-5 h-5 text-blue-500" />
    case 'new_comment': return <MessageCircle className="w-5 h-5 text-green-500" />
    case 'new_reply': return <MessageCircle className="w-5 h-5 text-tessan-primary" />
    case 'subscription_update': return <Users className="w-5 h-5 text-purple-500" />
    default: return <Bell className="w-5 h-5 text-gray-400" />
  }
}

export default function Index({ notifications, unread_count }: NotificationsProps) {
  const handleMarkAsRead = (id: number) => {
    router.post(`/notifications/${id}/mark_as_read`, {}, { preserveScroll: true })
  }

  const handleMarkAllAsRead = () => {
    router.post('/notifications/mark_all_as_read', {}, { preserveScroll: true })
  }

  return (
    <MainLayout title="Notifications">
      <Head title="Notifications" />

      <div className="w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold text-gray-900">Notifications</h1>
            {unread_count > 0 && (
              <p className="text-sm text-gray-500 mt-1">
                {unread_count} non lue{unread_count > 1 ? 's' : ''}
              </p>
            )}
          </div>
          {unread_count > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm text-tessan-primary hover:bg-tessan-primary/10 rounded-lg transition-colors"
            >
              <CheckCheck className="w-4 h-4" />
              Tout marquer comme lu
            </button>
          )}
        </div>

        {/* Notifications list */}
        {notifications.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
            <Bell className="w-16 h-16 mx-auto mb-4 text-gray-200" />
            <p className="text-gray-500 text-lg">Aucune notification</p>
            <p className="text-sm text-gray-400 mt-1">Vous serez notifié des mentions et réponses</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`flex items-start gap-4 p-4 transition-colors ${
                  !notification.read ? 'bg-blue-50/50' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  {getNotificationIcon(notification.type)}
                </div>

                <div className="flex-1 min-w-0">
                  <Link
                    href={notification.url}
                    onClick={() => !notification.read && handleMarkAsRead(notification.id)}
                    className="block group"
                  >
                    <p className={`text-sm ${!notification.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                      {notification.message}
                    </p>
                    {notification.data?.text_preview && (
                      <p className="text-sm text-gray-500 mt-0.5 truncate">
                        "{notification.data.text_preview}"
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      {formatRelativeDate(notification.created_at)}
                    </p>
                  </Link>
                </div>

                <div className="flex-shrink-0 flex items-center gap-1">
                  {!notification.read && (
                    <button
                      onClick={() => handleMarkAsRead(notification.id)}
                      className="p-1.5 text-gray-400 hover:text-tessan-primary hover:bg-gray-100 rounded transition-colors"
                      title="Marquer comme lu"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                  <Link
                    href={notification.url}
                    className="p-1.5 text-gray-400 hover:text-tessan-primary hover:bg-gray-100 rounded transition-colors"
                    title="Voir"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  )
}
